import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { defaultAction } from "@actions/defaultAction";
import CartOrderTotal from "@components/CartOrderTotal";
import CardInBigBuyCart from "@components/CardInBigBuyCart";
import PaypalCreditCartTag from "@components/PaypalCreditCartTag";
import {
  UPDATE_BUY_ORDER_TOTAL,
  UPDATE_BUY_CART_ERROR_MSG
} from "@constants/actionTypes";
import MotionSensor from "@modules/Common/MotionSensor";
import CartCheckoutButton from "@components/CartCheckoutButton";
import CartTotalValueItem from "@components/CartTotalValueItem";
import formattedRound from "@utils/formattedRound";
import { getCartTotals } from "@modules/Cart/selector";
import CartInfoGrid from "@components/CartInfoGrid";
import { useDispatch, useSelector } from "react-redux";

const MainContent = styled.div`
  min-height: 307px;
  position: relative;

  @media (max-width: 1100px) {
    min-height: 0px;
  }
`;

const ScrollGradient = styled.div`
  top: ${props =>
    props.scrolled < 123
      ? `${-69 + props.scrolled}px`
      : `${-69 + props.scrolled - 55}px`};
  position: absolute;
  width: 100%;
  background-image: linear-gradient(
    to bottom,
    rgba(250, 250, 250, 0.63),
    rgba(250, 250, 250, 0)
  );
  height: 56px;
  z-index: 1;
  pointer-events: none;

  @media (max-width: 1100px) {
    display: none;
  }
`;

const MainContainerGrid = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 2fr 1fr;
  margin: 0 auto;

  @media (max-width: 1750px) {
    grid-gap: 20px;
  }
  @media (max-width: 1500px) {
    grid-gap: 50px;
  }
  @media (max-width: 1450px) {
    grid-gap: 60px;
    grid-template-columns: auto auto;
  }
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    max-width: 850px;
  }
  @media (max-width: 950px) {
    width: 100%;
  }
  @media (max-width: 450px) {
    grid-gap: 30px;
  }
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 150px 1fr 125px 1fr 1fr 88px;
  grid-gap: 15px;

  @media (max-width: 1650px) {
    grid-template-columns: 71px 150px 1fr 125px 1fr 1fr 88px;
  }
  @media (max-width: 1500px) {
    grid-template-columns: 67px 137px 83px 1fr 75px 99px 76px;
  }
  @media (max-width: 1390px) {
    grid-template-columns: 67px 128px 69px 96px 1fr 1fr 76px;
  }
  @media (max-width: 1100px) {
    grid-template-columns: 1fr 128px 1fr 1fr 1fr 1fr 88px;
  }
  @media (max-width: 1000px) {
    grid-template-columns: 67px 128px 1fr 97px 95px 1fr 88px;
  }
  @media (max-width: 900px) {
    grid-template-columns: 1fr 128px 1fr 97px 95px 85px 88px;
  }
  @media (max-width: 835px) {
    grid-template-columns: 56px 90px 1fr 88px 1fr 75px 77px;
  }
  @media (max-width: 700px) {
    display: none;
  }
`;

const Header = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: #777984;
`;

const LargeScreen = styled.div`
  @media (max-width: 1100px) {
    display: none;
  }
`;

const TabletScreen = styled.div`
  @media (min-width: 1101px) {
    display: none;
  }
`;

const MobileCheckoutButtonContainer = styled.div`
  @media (min-width: 1101px) {
    display: none;
  }
  position: fixed;
  bottom: 0px;
  width: 100%;
  z-index: 102;
  background-color: #ffffff;
  padding-top: 30px;
  padding-bottom: 30px;
  border-top: 1px solid rgba(119, 121, 132, 0.15);

  @media (max-height: 700px) {
    padding-top: 25px;
    padding-bottom: 25px;
  }
  @media (max-height: 650px) {
    padding-top: 16px;
    padding-bottom: 16px;
  }
`;

const MobileCheckoutWidthContainer = styled.div`
  max-width: 850px;
  margin: 0 auto;

  @media (max-width: 950px) {
    width: 100%;
  }
  @media (max-width: 400px) {
    padding-right: 10px;
    padding-left: 10px;
  }
`;

const headers = ["", "Store Name", "Quantity", "Type", "Value", "% Off", ""];

const isScrolled = () => global.scrollY;

const CartMainContent = () => {
  const dispatch = useDispatch();
  const merchantsBuy = useSelector(state => state.merchantsBuy);
  const cardsBuy = useSelector(state => state.cardsBuy);
  const merchantsNew = useSelector(state => state.merchantsNew);
  const cartTotals = useSelector(state =>
    getCartTotals({
      cardsBuy: state.cardsBuy,
      coupon: state.coupon,
      paymentType: state.userActions.paymentType
    })
  );
  const cartBuyOrderTotal = useSelector(state => state.cartBuy.orderTotal);

  const [scrolled, setScrolled] = useState(isScrolled());

  useEffect(() => {
    const sensor = new MotionSensor({ delay: 0 }).sense();

    sensor.watch(() => {
      setScrolled(isScrolled());
    });

    return () => {
      sensor.ignore();
    };
  }, []);

  useEffect(() => {
    if (cartTotals.totalFaceValue < 15 && cartTotals.thereIsPhysical) {
      dispatch(
        defaultAction(
          UPDATE_BUY_CART_ERROR_MSG,
          "Order total does not meet minimum order total of $15.00"
        )
      );
    } else {
      dispatch(defaultAction(UPDATE_BUY_CART_ERROR_MSG, ""));
    }
  }, [cartTotals.totalFaceValue]);

  useEffect(() => {
    if (cartTotals.orderTotal !== Number(cartBuyOrderTotal)) {
      dispatch(
        defaultAction(UPDATE_BUY_ORDER_TOTAL, cartTotals.orderTotal + "")
      );
    }
  }, [cartTotals.orderTotal]);

  let allCardsInCart = [];

  const newCardArray = Object.values(cardsBuy);
  const merchantsLength = Object.keys(merchantsBuy).length;
  const merchantsNewLength = Object.keys(merchantsNew).length;

  allCardsInCart = newCardArray.map((card, index) => {
    const merchantArray =
      card.type === "newEcode" ? merchantsNew : merchantsBuy;

    if (!merchantArray[card.merchant]) {
      return null;
    }

    let slug = "";
    if (card.type === "newEcode") {
      if (merchantsNewLength < 2) {
        return null;
      }
      slug = `new/${merchantArray[card.merchant].slug.slice(
        6,
        merchantArray[card.merchant].slug.indexOf("-cards")
      )}`;
    } else {
      if (merchantsLength < 3) {
        return null;
      }
      slug = merchantArray[card.merchant].slug;
    }

    return (
      <CardInBigBuyCart
        key={index}
        index={index}
        card={card}
        slug={slug}
        merchantArray={merchantArray}
      />
    );
  });

  return (
    <>
      <MobileCheckoutButtonContainer className="mobile-checkout-button-container">
        <div className={"main-container-page-width"}>
          <MobileCheckoutWidthContainer>
            <CartTotalValueItem
              title={"Total"}
              value={`${formattedRound(cartTotals.orderTotal)}`}
              totalValue={true}
              secondaryValue={`$${formattedRound(cartTotals.totalFaceValue)}`}
              cartTotals={cartTotals}
            />
            <CartCheckoutButton />
          </MobileCheckoutWidthContainer>
        </div>
      </MobileCheckoutButtonContainer>
      <div className={"main-container-page-width"}>
        <MainContainerGrid>
          <div>
            <MainContent>
              <ScrollGradient scrolled={scrolled} />
              <MainGrid>
                {headers.map((header, index) => (
                  <Header key={index}>{header}</Header>
                ))}
              </MainGrid>

              {allCardsInCart}
            </MainContent>
            <LargeScreen>
              <PaypalCreditCartTag />
              <CartInfoGrid />
            </LargeScreen>
          </div>
          <>
            <CartOrderTotal />
            <TabletScreen>
              <PaypalCreditCartTag />
              <CartInfoGrid />
            </TabletScreen>
          </>
        </MainContainerGrid>
      </div>
    </>
  );
};

export default CartMainContent;
